import * as React from 'react';
import { DateTime } from 'luxon';

import {
  Box,
  Badge,
  Chip,
  Collapse,
  List,
  ListSubheader,
  Stack,
  Typography,
  IconButton, Paper,
} from '@mui/material';

import AddIcon from '@mui/icons-material/PlaylistAdd';
import RemoveIcon from '@mui/icons-material/PlaylistRemove';
import FilterIcon from '@mui/icons-material/FilterAlt';

import { useIncidentReportData } from '../../core/context/IncidentReportDataContext';
import useGetFilteredEventReports from '../../core/api/hooks/event-report/useGetFilteredEventReports';
import { EventReport, EventReportFilter, IncidentStatus } from '../../core/api/clients';

import IncidentFormReportListItem from './IncidentFormReportListItem';
import { FormListOperationsProvider } from '../../core/context/FormListOperationsContext';

import ListOperationsContainer from './ListOperationsContainer';
import UnattachedEventReportsWithFilter from './UnattachedEventReportsWithFilter';
import IncidentFormEventFilterSubheader from './IncidentFormEventFilterSubheader';

interface IncidentFormReportsProps {
  attachedReports?: EventReport[] | null;
  incidentStatus?: IncidentStatus;
  primaryReportId?: number | null;
  readOnly?: boolean;
}

function IncidentFormReports({ attachedReports, incidentStatus, primaryReportId, readOnly }: IncidentFormReportsProps): React.JSX.Element {
  const { incidentReport, addToIncident, removeFromIncident } = useIncidentReportData();

  const sortAttachedReports = (a: EventReport, b: EventReport): number => (a.id === primaryReportId ? -1 : b.order! - a.order!);

  const handleSecondaryActionRemove = (report: EventReport) => {
    if (attachedReports && attachedReports.length > 1) {
      removeFromIncident(incidentReport, report);
    }
  };

  const handleSecondaryActionAdd = (report: EventReport) => {
    addToIncident(incidentReport, report);
  };

  return (
    <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
      <List
        dense
        sx={{ flexBasis: '50%', flexGrow: 1 }}
        subheader={(
          <ListSubheader>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1">
                Attached Reports
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip label={`${attachedReports?.length ?? ''} Reports`} />
              </Stack>
            </Box>
          </ListSubheader>
        )}
      >
        {attachedReports?.sort(sortAttachedReports).map((report) => (
          <IncidentFormReportListItem
            key={report.id}
            report={report}
            enableSecondaryAction={attachedReports && attachedReports.length > 1}
            hideSecondaryAction={incidentStatus !== IncidentStatus.Open || readOnly}
            secondaryAction={() => handleSecondaryActionRemove(report)}
            secondaryActionIcon={(
              <RemoveIcon
                color={attachedReports && attachedReports.length > 1 ? 'error' : 'disabled'}
              />
            )}
            secondaryActionTitle="Remove Report"
          />
        ))}
      </List>
      {incidentStatus === IncidentStatus.Open && !readOnly && (
        <Box
          flexBasis="50%"
          flexGrow={1}
        >
          <UnattachedEventReportsWithFilter
            initialFilters={[
              { field: 'aircraftNumber', value: incidentReport.primaryReport?.aircraftNumber ?? '' },
              { field: 'flightNumber', value: incidentReport.primaryReport?.flightNumber ?? '' },
              { field: 'eventDate',
                value: (() => {
                  if (incidentReport.primaryReport?.eventDate) {
                    return DateTime.fromISO(incidentReport.primaryReport.eventDate).toFormat('MM/dd/yyyy');
                  }
                  return '';
                })() },
            ]}
            render={({ activeFilters, eventReports, onFilterAdd, onFilterRemove }) => (
              <List
                dense
                subheader={(
                  <IncidentFormEventFilterSubheader
                    activeFilters={activeFilters}
                    onFilterAdd={onFilterAdd}
                    onFilterRemove={onFilterRemove}
                    filterKeys={[
                      'reportType',
                      'eventType',
                      'eventType2',
                      'eventType3',
                      'flightNumber',
                      'aircraftNumber',
                      'landingAirport',
                      'departureAirport',
                      'scheduledArrivalAirport',
                      'eventDate',
                    ]}
                  />
              )}
              >
                {eventReports.map((eventReport) => (
                  <IncidentFormReportListItem
                    key={eventReport.id}
                    report={eventReport}
                    enableSecondaryAction
                    secondaryAction={() => handleSecondaryActionAdd(eventReport)}
                    secondaryActionIcon={<AddIcon color="success" />}
                    secondaryActionTitle="Add Report"
                  />
                ))}
              </List>
            )}
            attachedReports={attachedReports ?? []}
          />
        </Box>
      )}
    </Box>
  );
}

export default IncidentFormReports;
